import { Address, ShippingMethodType } from "@core/types";
import { IonToggle } from "@ionic/react";
import React from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useGetCurrentAddress from "../../../hooks/useGetCurrentAddress";
import { CartFormValues } from "./types";
import { getCourierServiceToggleText } from "../../../utils/getCourierServiceToggleText";
import { useConfig } from "../../../providers/config";
import { useOrderData } from "../../../providers/orderDataStore";
import cx from "clsx";
import { isFastCourierFeatureEnabled } from "../../../providers/address/taxiMasterShippingUtils";
import { DeliveryWarnings } from "./delivery-warnings";

interface CourierServiceToggleProps {
  control: Control<CartFormValues>;
  isCalculateTaxiShippingPending: boolean;
  handleCalculateTaxiShipping: ({ isTurned, address }: { isTurned: boolean; address: Address | null }) => Promise<void>;
}

export const CourierServiceToggle = ({
  control,
  handleCalculateTaxiShipping,
  isCalculateTaxiShippingPending,
}: CourierServiceToggleProps) => {
  const config = useConfig();
  const { t } = useTranslation();
  const customerAddress = useGetCurrentAddress();

  const { shippingMethod, taxiQuote } = useOrderData();
  const shippingMethodCost = shippingMethod?.cost;
  const shippingMethodType = shippingMethod?.methodType;
  const isTaxiMasterEnabled = isFastCourierFeatureEnabled(config);
  const isTaxiMasterTurned = !!(isTaxiMasterEnabled && shippingMethodType === ShippingMethodType.taxiMaster);

  const isDisabled = !customerAddress || isCalculateTaxiShippingPending;

  const courierServiceToggleText = getCourierServiceToggleText({
    isTaxiMasterTurned,
    hasAddress: !!customerAddress,
    isCalculateTaxiShippingPending,
    shippingMethodCost: shippingMethodCost ? parseInt(shippingMethodCost) : null,
  });

  const hasDeliveryWarnings = !!(isTaxiMasterTurned && taxiQuote?.warnings?.length);

  return (
    <Controller
      name="courierService"
      control={control}
      render={({ field: { onChange, value } }) => (
        <div className="flex items-start justify-between border-b-1 border-gray-200 px-2.5 py-4">
          <div className="flex flex-col gap-1 ">
            <div className={cx("text-[#292929]", isTaxiMasterTurned && "font-bold")}>{t("Send immediately")}</div>
            <div className={cx("text-xs text-[#656565]", isTaxiMasterTurned && "font-bold")}>
              {courierServiceToggleText}
              <div>{t("Delivery is performed by the external courier service")}</div>
              <div>{t("Payment is only by cashless")}</div>
              {hasDeliveryWarnings && taxiQuote?.warnings ? (
                <DeliveryWarnings warnings={taxiQuote.warnings} />
              ) : null}
            </div>
          </div>

          <IonToggle
            mode="ios"
            color="success"
            checked={value}
            disabled={isDisabled}
            onIonChange={(e) => {
              const newValue = e.detail.checked;
              console.log({ newValue, value });
              if (newValue === value) return;
              onChange(newValue);
              handleCalculateTaxiShipping({ isTurned: newValue, address: customerAddress });
            }}
          />
        </div>
      )}
    />
  );
};
